import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  fetchReviews as fetchReviewsService,
  submitReviewReply as submitReviewReplyService,
  deleteReviewReply as deleteReviewReplyService
} from '../services/reviewService';

const ReviewContext = createContext();

export const ReviewProvider = ({ children }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getReviews = useCallback(async (businessType, businessId, token) => {
    if (!businessId || !businessType) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchReviewsService(businessType, businessId, token);
      setReviews(data || []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setError(err.response?.data?.error || 'Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  const replyToReview = async (reviewId, comment, token) => {
    try {
      const res = await submitReviewReplyService(reviewId, comment, token);
      if (res.review) {
        setReviews((prev) =>
          prev.map((r) => (r._id === reviewId ? { ...r, reply: res.review.reply } : r))
        );
      }
      return { success: true, message: res.message };
    } catch (err) {
      console.error('Failed to post reply:', err);
      const message = err.response?.data?.error || 'Failed to submit reply';
      return { success: false, message };
    }
  };

  const removeReply = async (reviewId, token) => {
    try {
      const res = await deleteReviewReplyService(reviewId, token);
      setReviews((prev) =>
        prev.map((r) => (r._id === reviewId ? { ...r, reply: undefined } : r))
      );
      return { success: true, message: res.message };
    } catch (err) {
      console.error('Failed to delete reply:', err);
      const message = err.response?.data?.error || 'Failed to delete reply';
      return { success: false, message };
    }
  };

  return (
    <ReviewContext.Provider
      value={{
        reviews,
        loading,
        error,
        getReviews,
        replyToReview,
        removeReply,
        setReviews
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
};

export const useReviewContext = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReviewContext must be used within a ReviewProvider');
  }
  return context;
};

export default ReviewContext;
